import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { IoArrowBack, IoDocumentTextOutline, IoShieldCheckmarkOutline, IoBusinessOutline } from 'react-icons/io5'

const LAST_UPDATE = 'Juin 2026'

const MENTIONS_LEGALES = `
## Éditeur

**ALTEVO**
Représentant légal : Maxime BEHR
SIREN : 990348351 — SIRET : 99034835100018
Email : b.maxime6711@gmail.com

## Hébergement

Les données de santé sont hébergées en France chez un prestataire certifié **Hébergeur de Données de Santé (HDS)** conformément à l'article L.1111-8 du Code de la Santé Publique.

## Données de santé

MediSync est une application destinée exclusivement aux professionnels de santé. Elle traite des données de santé à caractère personnel au sens du RGPD (Art. 9) et de la loi Informatique et Libertés.

**Responsable de traitement :** ALTEVO (Maxime BEHR)
**Contact :** b.maxime6711@gmail.com

## Propriété intellectuelle

L'ensemble du contenu de l'application MediSync (code, design, textes, logos) est la propriété exclusive d'ALTEVO. Toute reproduction, même partielle, est interdite sans autorisation écrite préalable.

## Droit applicable

Le présent site et l'application sont soumis au droit français. En cas de litige, les tribunaux français sont seuls compétents.
`

const CGU = `
## 1. Objet et éditeur

Les présentes conditions générales d'utilisation (CGU) définissent les modalités d'accès et d'utilisation de l'application MediSync, éditée par ALTEVO (Maxime BEHR), SIREN 990348351, email : b.maxime6711@gmail.com.

## 2. Acceptation des conditions

L'utilisation de MediSync implique l'acceptation pleine et entière des présentes CGU. Si vous n'acceptez pas ces conditions, vous ne devez pas utiliser l'application.

## 3. Description du service

MediSync est une application de gestion de soins pour les infirmiers libéraux (IDEL) permettant :
- La gestion des dossiers patients
- La planification des tournées et visites à domicile
- La messagerie sécurisée entre professionnels de santé
- La gestion et le partage de documents patients
- Le suivi de l'Identité Nationale de Santé (INS)

## 4. Conditions d'accès

L'accès à MediSync est réservé exclusivement aux professionnels de santé disposant d'un numéro RPPS valide, authentifiés via **Pro Santé Connect (PSC)**. Vous êtes responsable de la confidentialité de vos identifiants et de toute activité effectuée sous votre compte.

## 5. Obligations de l'utilisateur

En utilisant MediSync, vous vous engagez à :
- Fournir des informations exactes et à jour
- Respecter le secret médical et la confidentialité des données patients
- N'accéder qu'aux dossiers des patients dont vous avez la charge
- Ne pas utiliser l'application à des fins illicites ou non professionnelles
- Signaler immédiatement tout incident de sécurité à b.maxime6711@gmail.com

## 6. Données de santé

En tant que professionnel de santé, vous restez responsable des données patients que vous saisissez. MediSync agit en qualité de **sous-traitant** au sens de l'article 28 du RGPD. Les données sont hébergées chez un hébergeur certifié HDS conformément à l'article L.1111-8 du Code de la Santé Publique.

## 7. Propriété intellectuelle

L'ensemble des éléments de l'application (design, textes, logos, code) sont la propriété exclusive d'ALTEVO. Toute reproduction est interdite sans autorisation préalable écrite.

## 8. Responsabilité

ALTEVO s'efforce de maintenir l'application accessible, mais ne peut garantir une disponibilité permanente. L'utilisateur reste seul responsable de ses actes professionnels et de l'exactitude des données saisies.

## 9. Modification des CGU

ALTEVO se réserve le droit de modifier les présentes CGU à tout moment. Les utilisateurs seront notifiés de toute modification substantielle par email.

## 10. Droit applicable

Les présentes CGU sont régies par le droit français. Tout litige relève de la compétence des tribunaux français.

**Contact :** b.maxime6711@gmail.com
`

const POLITIQUE_CONFIDENTIALITE = `
## 1. Responsable du traitement

**ALTEVO — Maxime BEHR**
SIREN : 990348351 — SIRET : 99034835100018
Email : b.maxime6711@gmail.com

## 2. Données collectées

**Données des professionnels de santé :**
- Identité (nom, prénom, email)
- Numéro RPPS et numéro de licence
- Informations de cabinet (nom, adresse)
- Disponibilités professionnelles
- Logs de connexion et d'accès aux dossiers

**Données patients (traitées pour le compte de l'infirmier) :**
- Identité civile et INS (Identité Nationale de Santé)
- Informations de contact et adresse du domicile
- Données médicales, ordonnances et prescriptions
- Documents et fichiers de soins
- Données de tournée et de planning

## 3. Finalités et bases légales

| Traitement | Base légale | Article RGPD |
|---|---|---|
| Fourniture du service | Exécution du contrat | Art. 6(1)(b) |
| Données de santé patients | Soins et prise en charge | Art. 9(2)(h) |
| Logs de sécurité et d'accès | Intérêt légitime | Art. 6(1)(f) |
| Facturation | Obligation légale | Art. 6(1)(c) |

## 4. Durées de conservation

- Données de compte infirmier : durée du contrat + 3 ans
- Dossiers patients et données de santé : **20 ans** (obligation légale)
- Données de tournée : 1 an
- Logs d'accès aux dossiers : **5 ans** (obligation légale santé)
- Données de facturation : 10 ans

## 5. Destinataires des données

Vos données peuvent être transmises à :
- Notre hébergeur certifié HDS (sous-traitant technique)
- Les autorités compétentes sur réquisition judiciaire

**Aucune donnée n'est vendue ni cédée à des tiers à des fins commerciales.**

## 6. Hébergement HDS

Les données sont hébergées en France chez un prestataire certifié **Hébergeur de Données de Santé (HDS)**, conformément à l'article L.1111-8 du Code de la Santé Publique.

## 7. Vos droits (RGPD)

Conformément aux articles 15 à 22 du RGPD, vous disposez des droits suivants (délai de réponse : 1 mois) :
- **Droit d'accès** à vos données personnelles
- **Droit de rectification** des données inexactes
- **Droit à l'effacement** (sauf obligations légales de conservation)
- **Droit à la limitation** du traitement
- **Droit à la portabilité** (export au format JSON)
- **Droit d'opposition** au traitement

Pour exercer vos droits : **b.maxime6711@gmail.com**

Vous pouvez également introduire une réclamation auprès de la **CNIL** (www.cnil.fr).

## 8. Sécurité

Mesures de sécurité mises en œuvre :
- Chiffrement des données en transit (TLS 1.2+) et au repos (AES-256)
- Authentification forte via Pro Santé Connect (PSC — carte CPS / e-CPS)
- Journalisation infalsifiable de tous les accès aux données de santé (rétention 5 ans)
- Hébergement certifié HDS
- Gestion des vulnérabilités et mises à jour de sécurité sous 72h

## 9. Contact

**Responsable de traitement / DPO :** b.maxime6711@gmail.com
`

const TABS = [
  { id: 'legal', label: 'Mentions légales', icon: IoBusinessOutline, content: MENTIONS_LEGALES },
  { id: 'cgu', label: "Conditions d'utilisation", icon: IoDocumentTextOutline, content: CGU },
  { id: 'privacy', label: 'Politique de confidentialité', icon: IoShieldCheckmarkOutline, content: POLITIQUE_CONFIDENTIALITE },
] as const

type TabId = (typeof TABS)[number]['id']

function renderMarkdown(text: string) {
  const lines = text.trim().split('\n')
  const elements: React.ReactNode[] = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i]
    const trimmed = line.trim()

    if (trimmed.startsWith('## ')) {
      elements.push(
        <h2 key={i} className='text-lg font-semibold text-gray-900 dark:text-white mt-6 mb-2'>
          {trimmed.slice(3)}
        </h2>
      )
    } else if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
      elements.push(
        <p key={i} className='font-semibold text-gray-800 dark:text-gray-200 mt-3 mb-1'>
          {trimmed.slice(2, -2)}
        </p>
      )
    } else if (trimmed.startsWith('- ')) {
      elements.push(
        <li key={i} className='ml-5 text-gray-600 dark:text-gray-400 text-sm leading-relaxed list-disc'>
          {trimmed.slice(2)}
        </li>
      )
    } else if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      // Table — collect all rows
      const rows: string[][] = []
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        const row = lines[i].trim().slice(1, -1).split('|').map(c => c.trim())
        if (!row.every(c => /^[-:]+$/.test(c))) rows.push(row)
        i++
      }
      elements.push(
        <div key={`table-${i}`} className='overflow-x-auto my-4'>
          <table className='w-full text-sm border-collapse'>
            <thead>
              <tr className='bg-gray-100 dark:bg-gray-800'>
                {rows[0]?.map((cell, j) => (
                  <th key={j} className='px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700'>
                    {cell}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice(1).map((row, ri) => (
                <tr key={ri} className='even:bg-gray-50 dark:even:bg-gray-900/30'>
                  {row.map((cell, j) => (
                    <td key={j} className='px-3 py-2 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700'>
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
      continue
    } else if (trimmed === '') {
      elements.push(<div key={i} className='h-2' />)
    } else {
      // Inline bold **text**
      const parts = trimmed.split(/(\*\*[^*]+\*\*)/)
      elements.push(
        <p key={i} className='text-gray-600 dark:text-gray-400 text-sm leading-relaxed'>
          {parts.map((part, j) =>
            part.startsWith('**') && part.endsWith('**')
              ? <strong key={j} className='font-semibold text-gray-800 dark:text-gray-200'>{part.slice(2, -2)}</strong>
              : part
          )}
        </p>
      )
    }
    i++
  }
  return elements
}

export default function Legal() {
  const [searchParams] = useSearchParams()
  const initialTab = (searchParams.get('tab') as TabId | null) ?? 'legal'
  const [activeTab, setActiveTab] = useState<TabId>(
    TABS.some(t => t.id === initialTab) ? initialTab : 'legal'
  )
  const active = TABS.find(t => t.id === activeTab)!

  return (
    <div className='min-h-screen bg-gray-50 dark:bg-dark-50'>
      {/* Header */}
      <div className='bg-white dark:bg-dark-100 border-b border-gray-200 dark:border-gray-800'>
        <div className='max-w-4xl mx-auto px-6 py-4 flex items-center gap-4'>
          <Link to='/' className='inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors'>
            <IoArrowBack size={16} />
            Retour
          </Link>
          <span className='text-gray-300 dark:text-gray-600'>|</span>
          <span className='font-semibold text-gray-900 dark:text-white'>Informations légales — MediSync</span>
        </div>

        {/* Tabs */}
        <div className='max-w-4xl mx-auto px-6'>
          <div className='flex gap-1 overflow-x-auto'>
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                  activeTab === id
                    ? 'border-primary-600 text-primary-600 dark:text-primary-400 dark:border-primary-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                <Icon size={16} />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className='max-w-4xl mx-auto px-6 py-8'>
        <div className='bg-white dark:bg-dark-100 rounded-2xl border border-gray-200 dark:border-gray-800 p-8'>
          <div className='flex items-center justify-between mb-6 pb-4 border-b border-gray-100 dark:border-gray-800'>
            <h1 className='text-xl font-bold text-gray-900 dark:text-white'>{active.label}</h1>
            <span className='text-xs text-gray-400'>Mise à jour : {LAST_UPDATE}</span>
          </div>
          <div className='prose-sm max-w-none'>
            {renderMarkdown(active.content)}
          </div>
        </div>

        <p className='text-center text-xs text-gray-400 mt-6'>
          MediSync — Édité par ALTEVO (Maxime BEHR) · SIREN 990348351 · b.maxime6711@gmail.com
        </p>
      </div>
    </div>
  )
}
